// frontend/src/components/daytrader/dayTrader.ts
//
// Types (mirroring backend/src/api/routes/intraday.ts + day-risk.ts) and the
// pure helpers behind the day-trader widgets. Decision support only — nothing
// here produces a trade recommendation.

// ─── types ───────────────────────────────────────────────────────────────────

export type TradingSession = "asia" | "london" | "new-york" | "late";

export interface SessionChange { atSec: number; from: TradingSession; to: TradingSession }

export interface SessionResponse {
  success: boolean; error?: string;
  atSec: number; session: TradingSession; sessionStartSec: number; sessionEndSec: number;
  nextSessionChange: SessionChange; sessionChangeIn: number; nextFundingSec: number; fundingIn: number;
}

export interface VwapBands { vwap: number; stdev: number; upper1: number; lower1: number; upper2: number; lower2: number; anchorSec: number; candleCount: number }
export interface PriorDayLevels { dayStartSec: number; open: number; high: number; low: number; close: number }
export interface WeeklyOpen { weekStartSec: number; open: number; candleTime: number }
export interface WindowRange { startSec: number; endSec: number; high: number; low: number; complete: boolean; candleCount: number }

export interface IntradayLevelsBundle {
  atSec: number; tfSec: number; session: TradingSession; sessionStartSec: number; sessionEndSec: number;
  nextSessionChange: SessionChange; nextFundingSec: number; fundingIn: number;
  vwap: VwapBands | null; priorDay: PriorDayLevels | null; weeklyOpen: WeeklyOpen | null;
  asiaRange: WindowRange | null; openingRange: WindowRange | null;
}

export interface LevelDistance { key: string; price: number; pct: number; atr: number | null }

export interface LevelsResponse {
  success: boolean; error?: string;
  symbol: string; lastPrice: number;
  atr: { tf: string; period: number; value: number | null };
  levels: IntradayLevelsBundle;
  distances: LevelDistance[];
}

export interface DailyRiskConfig {
  accountSize: number; riskPerTradePct: number; maxDailyLossR: number; maxTradesPerDay: number; resetHourUtc: number;
}

export interface DailyRiskStatus {
  locked: boolean; reason: string | null;
  tradesToday: number; realizedR: number; remainingR: number; remainingTrades: number;
  openTrades: number; openRiskR: number; dayStartMs: number; nextResetMs: number;
  riskPerTradeUsd: number; config: DailyRiskConfig;
}

export interface PositionSize { qty: number; notional: number; riskUsd: number; costR: number; costUsd: number; stopPct: number }

// ─── session clock (mirrors backend/src/services/intraday/levels.ts) ────────

export const DAY_SEC = 86400;
export const HOUR_SEC = 3600;

export const SESSION_HOURS: Record<TradingSession, [number, number]> = {
  asia: [0, 7],
  london: [7, 13],
  "new-york": [13, 20],
  late: [20, 24],
};
export const SESSION_ORDER: TradingSession[] = ["asia", "london", "new-york", "late"];
export const FUNDING_HOURS = [0, 8, 16];

export const SESSION_META: Record<TradingSession, { label: string; short: string; bar: string; text: string }> = {
  asia:       { label: "Asia",     short: "ASIA", bar: "bg-sky-500/35",     text: "text-sky-700 dark:text-sky-400" },
  london:     { label: "London",   short: "LDN",  bar: "bg-violet-500/35",  text: "text-violet-700 dark:text-violet-400" },
  "new-york": { label: "New York", short: "NY",   bar: "bg-amber-400/35",   text: "text-amber-700 dark:text-amber-400" },
  late:       { label: "Late",     short: "LATE", bar: "bg-muted/20",           text: "text-muted" },
};

function dayStart(sec: number): number { return Math.floor(sec / DAY_SEC) * DAY_SEC; }

export function sessionOf(sec: number): TradingSession {
  const hour = Math.floor((sec - dayStart(sec)) / HOUR_SEC);
  for (const s of SESSION_ORDER) {
    const [a, b] = SESSION_HOURS[s];
    if (hour >= a && hour < b) return s;
  }
  return "late";
}

/** Next session boundary strictly after sec. */
export function nextSessionChangeAt(sec: number): SessionChange {
  const from = sessionOf(sec);
  const at = dayStart(sec) + SESSION_HOURS[from][1] * HOUR_SEC;
  return { atSec: at, from, to: sessionOf(at) };
}

/** Next funding time (00/08/16 UTC) strictly after sec. */
export function nextFundingAt(sec: number): number {
  const d = dayStart(sec);
  for (const h of FUNDING_HOURS) {
    const t = d + h * HOUR_SEC;
    if (t > sec) return t;
  }
  return d + DAY_SEC;
}

/** Position of sec within its UTC day, 0..1. */
export function dayFraction(sec: number): number {
  return (sec - dayStart(sec)) / DAY_SEC;
}

/** 3725 → "1:02:05", 65 → "01:05". Negative clamps to 0. */
export function formatCountdown(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(r).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "HH:MM" UTC for an epoch-second value. */
export function utcHm(sec: number): string {
  const d = new Date(sec * 1000);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

// ─── levels ──────────────────────────────────────────────────────────────────

export type LevelGroup = "vwap" | "priorDay" | "weekly" | "asia" | "opening";

/** `color` is a theme token (src/index.css): use `var(color)` in CSS, PriceChart resolves it for the canvas. */
export const LEVEL_META: Record<string, { label: string; group: LevelGroup; color: string; dashed: boolean }> = {
  vwapUpper2:       { label: "VWAP +2σ",   group: "vwap",     color: "--chart-vwap",      dashed: true },
  vwapUpper1:       { label: "VWAP +1σ",   group: "vwap",     color: "--chart-vwap",      dashed: true },
  vwap:             { label: "VWAP",       group: "vwap",     color: "--chart-vwap",      dashed: false },
  vwapLower1:       { label: "VWAP −1σ",   group: "vwap",     color: "--chart-vwap",      dashed: true },
  vwapLower2:       { label: "VWAP −2σ",   group: "vwap",     color: "--chart-vwap",      dashed: true },
  priorDayHigh:     { label: "PD high",    group: "priorDay", color: "--chart-prior-day", dashed: false },
  priorDayOpen:     { label: "PD open",    group: "priorDay", color: "--chart-prior-day", dashed: true },
  priorDayClose:    { label: "PD close",   group: "priorDay", color: "--chart-prior-day", dashed: true },
  priorDayLow:      { label: "PD low",     group: "priorDay", color: "--chart-prior-day", dashed: false },
  weeklyOpen:       { label: "Week open",  group: "weekly",   color: "--chart-week",      dashed: false },
  asiaHigh:         { label: "Asia high",  group: "asia",     color: "--chart-session",   dashed: true },
  asiaLow:          { label: "Asia low",   group: "asia",     color: "--chart-session",   dashed: true },
  openingRangeHigh: { label: "OR high",    group: "opening",  color: "--chart-session",   dashed: true },
  openingRangeLow:  { label: "OR low",     group: "opening",  color: "--chart-session",   dashed: true },
};

export function levelLabel(key: string): string {
  return LEVEL_META[key]?.label ?? key;
}

/** Nearest level strictly above price (pct > 0) and at/below price (pct <= 0). */
export function nearestLevels(distances: LevelDistance[]): { above: string | null; below: string | null } {
  let above: LevelDistance | null = null;
  let below: LevelDistance | null = null;
  for (const d of distances) {
    if (!Number.isFinite(d.pct)) continue;
    if (d.pct > 0) { if (!above || d.pct < above.pct) above = d; }
    else if (!below || d.pct > below.pct) below = d;
  }
  return { above: above?.key ?? null, below: below?.key ?? null };
}

/** Levels sorted top (highest price) to bottom. Does not mutate the input. */
export function sortByPriceDesc(distances: LevelDistance[]): LevelDistance[] {
  return [...distances].sort((a, b) => b.price - a.price);
}

/**
 * `major` = the key levels, the only ones the chart draws unless the trader
 * asks for all. `color` is a theme token name (e.g. "--chart-vwap").
 */
export interface ChartLevel { price: number; title: string; color: string; dashed?: boolean; major?: boolean }

/**
 * Key levels: the ones day traders actually react to. The rest (VWAP bands,
 * yesterday's open/close, Asia and opening ranges) stay one click away; the
 * strategies built on them measured no edge (ORB, VWAP, Asia sweep).
 */
const MAJOR_LEVELS = new Set(["vwap", "priorDayHigh", "priorDayLow", "weeklyOpen"]);

export function chartLevelsFrom(distances: LevelDistance[] | null | undefined): ChartLevel[] {
  if (!distances) return [];
  return distances
    .filter(d => Number.isFinite(d.price) && d.price > 0)
    .map(d => {
      const m = LEVEL_META[d.key];
      return { price: d.price, title: m?.label ?? d.key, color: m?.color ?? "--chart-session", dashed: m?.dashed ?? true, major: MAJOR_LEVELS.has(d.key) };
    });
}

// ─── risk ────────────────────────────────────────────────────────────────────

export const MAX_COST_R = 0.25;
/** Fees + slippage, both sides (backend costModel: 5 + 2.5 bps per side). */
export const ROUND_TRIP_COST = 0.0015;

/** Round-trip costs as a share of the risk (R): what a stop-out really costs on top of 1R. */
export function costRFor(entry: number, stop: number): number {
  const risk = Math.abs(entry - stop);
  return risk > 0 && entry > 0 ? ROUND_TRIP_COST / (risk / entry) : Infinity;
}

export function costTooHigh(costR: number): boolean {
  return Number.isFinite(costR) && costR > MAX_COST_R;
}

/** Share of the daily loss budget already consumed (realized losses + open risk), 0..1. */
export function budgetUsedFraction(s: Pick<DailyRiskStatus, "remainingR" | "config">): number {
  const max = s.config.maxDailyLossR;
  if (!(max > 0)) return 0;
  return Math.min(1, Math.max(0, (max - s.remainingR) / max));
}

/** Parse a user-typed number; empty/invalid → null. Accepts "1,234.5". */
export function parseNum(v: string): number | null {
  const t = v.replace(/,/g, "").trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
